"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = {
    noExample: ``,
    hydra: `osc(10).out()`,
    sequences: `// there are four sequences seq0 ... seq3
seq0.init(
    osc(),
    noise(),
    voronoi()
)
//.duration(1, 2, .5) // changes the duration of each scene
.out(o0)
render(o0)`,
    textureFormat: `
    /*
     * Each base texture is in rgba8 (ubyte 0-255) format with linear filtering. 
     * When an integer format is chosen (e.g., rgba8i, rgba16i, rgba32i, rgba32ui), linear filtering is not supported and is automatically changed to nearest filtering.
     * Possibile texture internalFormats:
     * - rgba8 (default)
     * - rgba8i
     * - rgba16i
     * - rgba32i
     * - rgba16ui
     * - rgba32ui
     * - rgba16f
     * - rgba32f
     * 
     * Possible texture filters:
     * - linear (default)
     * - nearest
     */
o1.internalFormat("rgba8i") // automatically sets filter to nearest
o2.internalFormat("rgba32i")
o3.internalFormat("rgba32f")

s1.initText("o0 rgba8 linear (default)", "100px Arial")
s0.initText("o1 rgba8i nearest", "100px Arial")
s2.initText("o2 rgba32i nearest", "100px Arial")
s3.initText("o3 rgba32f linear", "100px Arial")

src(o0).diff(shape(4, .5).scale([0, 1, .5, 2])).rotate(.05).out(o0)
src(o0).layer(src(s0).mult(solid(1,0,0)).translate([.3,-.4])).out(o4)

src(o1).diff(shape(4, .5).scale([0, 1, .5, 2])).rotate(.05).out(o1)
src(o1).layer(src(s1).mult(solid(1,0,0)).translate([.2,-.4])).out(o5)

src(o2).diff(shape(4, .5).scale([0, 1, .5, 2])).rotate(.05).out(o2)
src(o2).layer(src(s2).mult(solid(1,0,0)).translate([.35,-.4])).out(o6)

src(o3).diff(shape(4, .5).scale([0, 1, .5, 2])).rotate(.05).out(o3)
src(o3).layer(src(s3).mult(solid(1,0,0)).translate([.28,-.4])).out(o7)
render(o4, o5, o6, o7)
`,
    "3d": {
        model: `/* Renders a simple cube to the scene.
* Drag the mouse to move the camera. Press Ctrl + Shift + H to toggle editor visibility. 
* Zoom with Command+Scroll to prevent editor scrolling */
cube().out()


/* Rendering
subdivisions = 1 // Increase the subdivisions to add more vertices.
cube({ nx: subdivisions, ny: subdivisions, nx: subdivisions })
.wireframe(1) // Comment out or set to 0 to deactivate.
//.point(10) // Enable points to draw only the vertices.
.out()
*/

/* Texture feedback
cube()
.fragment(
	solid(1,1,1).diff(o0)
)
.out(o0)
*/


/* Colorizes a mesh.
* A list of all available geometries is defined here:
* https://github.com/dmnsgn/primitive-geometry 
sphere({ nx: 200 })
.fragment(
	osc() // You can also try 'pbr()' or 'flat()'. I'll implement other materials.
	// pbr()
	// flat()
)
.out()
*/


/* Modifies a mesh.
sphere({ nx: 200 })
.fragment(flat())
.vertex(
	// In the first iteration of vertex generation, the coordinates are set to the 'position' attribute.
	// You can log the generated vertex shader to the console with:
	// ll(o0.shader.vertexGenerated)
	 st() //it's an alias for src('vec4(_st, .1)'); 'st' is the current vertex position.
	.modulate(noise(2, 1, 1)) // The third parameter of noise is the RGB shift, similar to osc.
)
.out()
*/`,
        instancing: `/* create instance */
cube()
.fragment(flat())
.vertex(
    st()
    .scale(.2)
    // instanceOffset is from 0 to 1, distributing on the X axis. INSTANCES is the number of instances.
    .translateX('(instanceOffset * 2. - 1.) * float(INSTANCES)') 
    .rotate(0, 'instanceOffset') // from left to right, the speed of rotation increases
)
.instances(10)
.out()`,
        camera: `/* create multiple cameras */
c = camera('perspective') // creates a new perspective camera (orthographic is a work in progress)
c.dispose()
c.position([0,0,8])

cube()
.fragment(st())
.camera(c)
.out()

sphere().wireframe().out(o1)

solid()
.add(o0).add(o1)
.out(o2)
render(o2)

/* create a new camera 
c = camera()
c.activate()
c.position([0,0,100])
c.screenPanning(false) // when false, the Y-axis pans relative to the camera's orientation; otherwise, Y is fixed to the 
tetrahedron()
    .fragment(st())
    .vertex(st()
        .translate(
            // move based on instance offset
            'vec3(rand(instanceOffset, -distance, distance), 0., rand(instanceOffset * 1000., -distance, distance))'
        )
        .scale('(noise3d(instanceOffset) + 1. / 2.) + 1.')
        .require('rand', 'noise3d'))
    .uniform('distance', 1000) // bind uniform to fragment and vertex shaders
    .instances(300_000)
    .out()
render(o0)
*/`,
        gltf: `// load a gltf model
suzanne = await gltf('/assets/examples/models/suzanne/suzanne.gltf')

suzanne.out()


/* move mesh 
suzanne.vertex(
    st()
    .translateZ(() => cos(time) * 1)
    .modulate(osc(6, .2, 1).rotate(0, 1), .1)
).out()
*/


/* change default material 
mask = suzanne.pbr().thresh(.3, .1)
suzanne
    .vertex(st().scale(.5))
    .fragment(
        suzanne.pbr() // pbr() with loaded material properties
        .add(solid(1).mult(mask))
        .luma(.2, .2)
    )
    .out()
osc(4, 1).rotate(PI / 2).modulateScale(solid().add(gradient().g())).out(o1) // use o1 to modulate the mesh
src(o0).modulateScale(o1).out(o2)
// render(o0, o1, o2) // render multiple textures at the same time
render(o2)
*/


/* change initial fragment coordinate 
suzanne
    .useTexCoord(true) // change to "false" to prevent using texCoord as the initial generation coordinate
    .fragment(osc())
    .out()
render(o0)
*/`,
        texture: `/* create texture from data */
chessSize = [8, 8]
chessTextureData = array(chessSize[0] * chessSize[1] * 4) // 4 [r,g,b,a]
    .chunk(4) // array of 4 chunk [[0,0,0,0], [0,0,0,0], ..., [0,0,0,0]]
    .map((v, i) => 
        (floor(i / chessSize[0]) + (i % chessSize[0])) % 2 === 0 
            ? [255,255,255,255] 
            : [0,0,0,255]) // chessboard
    .flat() // chunk to linear array
    .to('ubyte') // transform Array to TypedArray, ubyte is UInt8 (0, 255)
    
log(chessTextureData) // Uint8Array(256)

t = texture({
    name: 'chessTexture',
    width: chessSize[0], height: chessSize[1],
    filter: 'nearest' // pixelate, change to "linear" to see the difference
}).init(chessTextureData)
render(t)


/* use float texture to move mesh 
arr = [
    [0, .5, 0, 1], 
    [1, 1, 1, 1],
    [0, 0, 1, 1], 
    [1, 0, 0, 1]
].flat().to('float')
positionTexture = texture({ 
    width: 2, height: 2,
    filter: 'nearest' // abs position, change to "linear" to see the difference
})
.init(arr)
render(positionTexture)


cube()
.fragment(flat())
.vertex(st().glslCoord(\`
    vec2 texSize = vec2(textureSize(positionTexture, 0));
    float col = mod(float(instance), texSize.x);
    float row = floor(float(instance) / texSize.x);
    vec2 coord = vec2(col, row) / texSize;
    vec3 texCoord = texture(positionTexture, coord).xyz;
    return _st + texCoord * 2. - 1.;
\`)
)
.uniform({
    positionTexture // try changing positionTexture arr to different values
})
.instances(2 * 2).out()
render(o0)
*/
`,
        cubemap: `/* cubemap in single file */
camera().reset()
earth = await cubemap('/assets/examples/images/earth.png')
sphere()
    .fragment(textureCube(earth))
    .useTexCoord(false)
    .out()



/* cubemap in multiple files 
skybox = cubemap([
    '/assets/examples/images/museum/pos-x.jpg',
    '/assets/examples/images/museum/neg-x.jpg',
    '/assets/examples/images/museum/pos-y.jpg',
    '/assets/examples/images/museum/neg-y.jpg',
    '/assets/examples/images/museum/pos-z.jpg',
    '/assets/examples/images/museum/neg-z.jpg',
])
camera().position([0, 0, 0.1])
textureCube(skybox)
    .glslCoord("(inverse(projectionMatrix * viewMatrix) * vec4(_st * 2. - 1., 1.)).xyz")
    .uniform([{
        name: 'skybox',
        type: 'samplerCube',
        value: skybox
    }])
    .uniform({
        ...camera().uniforms
    })
    .out()
*/`,
        textureFrames: `/* Create a texture with multiple frames (instances) */
// location.href.indexOf('debug') === -1 ? location.href += '&debug=1' : null // show debugger (wip)


src(\`vec4(mix(vec3(0.2,0.2,0.2), vec3(2.,0.5,0), _st.y), 1.)\`)
.layer(
    cube().frag(pbr()).vert(st().translateY('cos(time) * .1').rotateY('time * 2.'))
)
.out(o0)
input = o0

// await s0.initCam()
// input = s0

lines = 100 // number of texture instances
storageTexture = texture({ len: lines, name: 'storageTexture'})
outputTexture = texture('outputTexture')

lineHeight = ceil(storageTexture.height / lines)
tickIndex = 0 // *
update = () => {
    /* For performance reasons, only the visible output will be updated.
     * Call .update() (when the output is o0) to activate the cube shader.
     * To see it in action, comment out the line below and call render() without input. */
    input.activate(true)

    storageTexture.index = tickIndex % lines
    input.out(storageTexture) // Copy the input to the current storageTexture frame.
    for (let i = 0; i < lines; i++) {
        storageTexture.out(
            outputTexture, 0, i * lineHeight, width, lineHeight
        ) // Draw a line from the current storageTexture frame to the outputTexture.
        storageTexture.next() // Move to the next source frame in the storageTexture sequence.
    }
    tickIndex++;
}
render(outputTexture, input)
// render(outputTexture) // If called this way, the cube doesn't move.`,
    },
    "3d in depth": {
        "create a model": `// Generate a 2D plane.

// The VAO (Vertex Array Object) is a container that groups the data
// and configuration of a 3D object's geometry.
planeVAO = vao({
    attrs: { 
        pos: {
            data: [
                -0.5,-0.5, 0.0, // Vertex 0 (bottom left)
                0.5,-0.5, 0.0, // Vertex 1 (bottom right)
                0.5, 0.5, 0.0, // Vertex 2 (top right)
                -0.5, 0.5, 0.0, // Vertex 3 (top left)
            ], 
            size: 3,
        }
    },
    cells: [
        0, 1, 2, // First triangle (connects vertices 0, 1, and 2)
        0, 2, 3, // Second triangle (connects vertices 0, 2, and 3)
    ],
    elements: 6, // Total number of vertices to draw (from 6 indices)
    primitive: "triangles",
})
shader()
    .vao(planeVAO)
    // For each buffer in the VAO (e.g., 'pos'), the vertex shader generator
    // automatically adds the preprocessor define (HAS_POS), the attribute ('in vec3 pos'),
    // and the varying for the fragment shader ('out vec3 vPos'). The varying's
    // assignment, however, must be done manually (e.g., vPos = pos;).
    //
    // To inspect the generated code, you can use ll(o0.shader.vertexGenerated)
    .vertex(src(\`
        vPos = pos;  // pass 'pos' to the fragmentShader
        return vec4(pos, 1.)
    \`))
    .fragment(
        src('vec4((vPos + 0.5).xy, cos(time), 1.)')
        .scale(() => cos(time))
    )
    .uniform({ time })
    .out(o0) // for raw shaders, the output must be specified

/*
// Create a rotating cube
// function that generates a rotation matrix
createRotationYMatrix = (angle) => {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
 
  return [
    c, 0, s, 0,
    0, 1, 0, 0,
   -s, 0, c, 0,
    0, 0, 0, 1
  ];
}
// Define the positions of the 8 unique corners of the cube.
cubePositions = [
  // x,    y,    z
  -0.5, -0.5,  0.5, // 0: Front, bottom, left
   0.5, -0.5,  0.5, // 1: Front, bottom, right
   0.5,  0.5,  0.5, // 2: Front, top,  right
  -0.5,  0.5,  0.5, // 3: Front, top,  left
  -0.5, -0.5, -0.5, // 4: Back,  bottom, left
   0.5, -0.5, -0.5, // 5: Back,  bottom, right
   0.5,  0.5, -0.5, // 6: Back,  top,  right
  -0.5,  0.5, -0.5, // 7: Back,  top,  left
];
// Index of the faces
cubeIndices = [
    0, 1, 2,  0, 2, 3,  
    5, 4, 7,  5, 7, 6, 
    4, 0, 3,  4, 3, 7, 
    1, 5, 6,  1, 6, 2, 
    3, 2, 6,  3, 6, 7, 
    0, 4, 5,  0, 5, 1, 
];
// VAO creation
cubeVAO = vao({
    attrs: { 
        pos: { data: cubePositions, size: 3 } // the size is 3
    },
    cells: cubeIndices,
    elements: 36, // Total number of vertices to draw (from 36 indices)
    primitive: "triangles",
});
// Model creation
shader()
    .vao(cubeVAO)
    .vertex(\`
        in vec3 pos; // Input: vertex position from the buffer
        // Output: interpolated position to pass to the fragment shader, you can name it whatever you want
        // the important thing is not to forget the assignment in main
        out vec3 vPos;
        uniform mat4 modelMatrix;
        uniform mat4 viewMatrix;
        uniform mat4 projectionMatrix;
        void main() {
            vPos = pos; // Save the original position for the fragment shader
            gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(pos, 1.0);
        }
    \`)
    .fragment(\`
        in vec3 vPos; // Input: interpolated position received from the vertex shader
        out vec4 fragColor;
        void main() {
            // Use the vertex position to create a gradient color.
            // This visual trick helps to perceive the 3D shape and rotation.
            vec3 color = vPos * 0.5 + 0.5;
            fragColor = vec4(color, 1.0);
        }
    \`)
    .cullFace('back') // don't show the back faces
    .uniform({
        ...camera().uniforms,
        modelMatrix: () => createRotationYMatrix(time * 0.5),
    })
    .out(o0)
*/`,
        transformFeedback: `// Transform Feedback takes input data from a buffer,
// modifies it in the vertex shader, and saves the results to another buffer,
// completely skipping the coloring phase.
dataA =     [  1,  2,  3,   4,  5,  6,   7,  8,  9]
dataB =     [ 10,  9,  8,   7,  6,  5,   4,  3,  2]
expected =  [ 10, 18, 24,  28, 30, 30,  28, 24, 18] // result[i] = dataA[i] * dataB[i]
bufferA = buffer('bufA').data(dataA).size(1)
bufferB = buffer('bufB').data(dataB).size(1)
resultBuffer = buffer('resBuf').size(dataA.length).data(new Float32Array(dataA.length)).usage('dynamic read')
computeVAO = vao({
    attrs: {
        a: bufferA,
        b: bufferB,
    },
    // number of executions, for each execution the value of in_A and in_B will advance
    // based on the set size (1), so the shader will be executed 9 times
    count: dataA.length, 
    rasterizerDiscard: true, // avoids drawing
});
feedback = transformFeedback({
    buffers: {
        'res': resultBuffer 
    }
});
shader()
    .vao(computeVAO)
    .transformFeedback(feedback)
    .vertex(\`
        // Input from the VAO buffers
        in float a;
        in float b;
        // Output to be captured (the name must match feedback')
        out float res;
        void main() {
            // We perform the multiplication
            res = a * b;
        }
    \`)
    .out()

 resultArray = await resultBuffer.read();
 log(resultArray)
 ok = resultArray.every((v, i) => expected[i] === v)

 s0.initText(\`result [\${ok ? "ok" : "no"}]: \${resultArray}\`)
 s0.out(o0)`,
    },
};
